import React from 'react';
import { BUCKET_ORDER } from 'shared/src/wrkthru/status-codes';
import { BUCKETS, UNKNOWN_BUCKET_HEX } from './bucket-theme';
import type { PortfolioStatusCounts } from 'shared/src/wrkthru/types';

type PortfolioStatusBarProps = {
  counts: PortfolioStatusCounts;
};

type BarSegment = {
  key: string;
  label: string;
  count: number;
  pct: number;
  flatHex?: string;
  gradient?: { from: string; to: string };
  glow?: string;
  legendHex: string;
};

export function PortfolioStatusBar({ counts }: PortfolioStatusBarProps) {
  const barSegments = buildBarSegments(counts);

  // Unknown segment is deliberately excluded from the legend.
  const legendSegments = barSegments.filter(s => s.key !== 'unknown');

  return (
    <div className="space-y-2">
      <div
        className="flex w-full overflow-hidden rounded-full bg-slate-200"
        style={{ height: '14px' }}>
        {barSegments.map(s => (
          <div
            key={s.key}
            className="h-full"
            style={{
              width: `${s.pct}%`,
              background: segmentBackground(s),
              boxShadow: s.glow
            }}
            title={`${s.label}: ${s.count}`}
          />
        ))}
      </div>
      <Legend segments={legendSegments} />
    </div>
  );
}

function buildBarSegments(counts: PortfolioStatusCounts): BarSegment[] {
  const total = Math.max(counts.total, 1);

  const barSegments: BarSegment[] = BUCKET_ORDER.filter(b => counts.counts[b] > 0).map(b => {
    const accent = BUCKETS[b].accent;
    return {
      key: b,
      label: BUCKETS[b].label,
      count: counts.counts[b],
      pct: (counts.counts[b] / total) * 100,
      gradient: accent.gradient,
      glow: accent.glow,
      legendHex: accent.hex
    };
  });

  if (counts.unknown > 0) {
    barSegments.push({
      key: 'unknown',
      label: 'Not yet detected',
      count: counts.unknown,
      pct: (counts.unknown / total) * 100,
      flatHex: UNKNOWN_BUCKET_HEX,
      legendHex: UNKNOWN_BUCKET_HEX
    });
  }

  return barSegments;
}

function segmentBackground(s: BarSegment): string | undefined {
  return s.gradient ? `linear-gradient(90deg, ${s.gradient.from}, ${s.gradient.to})` : s.flatHex;
}

function Legend({
  segments
}: {
  segments: Array<Pick<BarSegment, 'key' | 'label' | 'count' | 'legendHex'>>;
}) {
  if (segments.length === 0) {
    return null;
  }
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
      {segments.map(s => (
        <li key={s.key} className="flex items-center gap-1.5">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ backgroundColor: s.legendHex }}
            aria-hidden="true"
          />
          <span className="font-medium text-slate-700">{s.count}</span>
          <span>{s.label}</span>
        </li>
      ))}
    </ul>
  );
}
